import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, MailQuestion } from "lucide-react";

/**
 * /verify-email?token=... handler.
 *
 * Flow:
 *  1. Read token from query string.
 *  2. Call GET /api/auth/verify-email?token=...
 *  3. On 200 → show success card + CTA "Sign in" → /login.
 *  4. On 4xx → show error + CTA "Request new link" → /verify-email/resend.
 */
export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "checking", message: "", email: "" });
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: t("verify.missing_token") });
      return;
    }
    api
      .get("/auth/verify-email", { params: { token } })
      .then((r) => {
        setState({
          status: r.data.already_verified ? "already" : "ok",
          message: "",
          email: r.data.email || "",
        });
      })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        setState({
          status: "error",
          message:
            (typeof detail === "string" && detail) || t("verify.invalid_or_expired"),
        });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-zinc-950 via-black to-zinc-950 p-6" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <Logo showText={false} />
          </Link>
        </div>

        <div className="border border-white/10 bg-zinc-900/40 rounded-xl p-8">
          {state.status === "checking" && (
            <div className="text-center" data-testid="verify-status-loading">
              <Loader2 className="mx-auto animate-spin text-orange-400 mb-4" size={36} />
              <p className="text-zinc-300">{t("verify.checking")}</p>
            </div>
          )}

          {(state.status === "ok" || state.status === "already") && (
            <div className="text-center" data-testid="verify-status-success">
              <div className="w-16 h-16 mx-auto bg-emerald-500/10 border-2 border-emerald-500/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="text-emerald-400" size={32} />
              </div>
              <h1 className="font-outfit font-black text-white text-2xl mb-2">
                {state.status === "already" ? t("verify.already_verified_title") : t("verify.verified_title")}
              </h1>
              <p className="text-zinc-400 text-sm mb-6">
                {t("verify.verified_subtitle_1")}{" "}
                <span className="text-orange-400 font-mono">{state.email}</span>{" "}
                {t("verify.verified_subtitle_2")}
              </p>
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => navigate("/login")}
                data-testid="verify-go-to-login"
              >
                {t("verify.verified_cta")}
              </Button>
            </div>
          )}

          {state.status === "error" && (
            <div className="text-center" data-testid="verify-status-error">
              <div className="w-16 h-16 mx-auto bg-red-500/10 border-2 border-red-500/30 rounded-full flex items-center justify-center mb-4">
                <XCircle className="text-red-400" size={32} />
              </div>
              <h1 className="font-outfit font-black text-white text-2xl mb-2">
                {t("verify.failed_title")}
              </h1>
              <p className="text-zinc-400 text-sm mb-6">{state.message}</p>
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => navigate("/verify-email/resend")}
                  data-testid="verify-resend-btn"
                >
                  <MailQuestion size={16} className="mr-2" /> {t("verify.request_new_link")}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-zinc-400 hover:text-white"
                  onClick={() => navigate("/login")}
                >
                  {t("verify.go_to_login")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
